/* AB ab_flex_offer — sunsets 2026-10-31, delete src/pages/AB_FlexOfferSuivi/ */

export type AB_BookingRow = {
    request_id: string;
    agency_id: string;
    agency_name: string;
    country_name: string;
    status_name_legacy: string;
    status_grouped: string;
    sale_date: string;
    trip_arrival_date: string;
    cancellation_deadline: string;
    days_until_deadline: string;
    trip_price: string;
    amount_paid: string;
    remaining_balance_to_pay: string;
};

export type AB_SortColumn = keyof AB_BookingRow;
export type AB_SortDirection = 'asc' | 'desc';
export type AB_PaymentFilter = '' | 'fully_paid' | 'account_done' | 'deposit_paid';

const NUM_COLS: ReadonlySet<string> = new Set([
    'request_id',
    'days_until_deadline',
    'trip_price',
    'amount_paid',
    'remaining_balance_to_pay',
]);
const DATE_COLS: ReadonlySet<string> = new Set([
    'sale_date',
    'trip_arrival_date',
    'cancellation_deadline',
]);

export function AB_parseCSV(text: string): AB_BookingRow[] {
    const lines = text.trim().split('\n');
    if (lines.length === 0) return [];
    const headers = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
    return lines
        .slice(1)
        .filter((l) => l.trim())
        .map((line) => {
            const values: string[] = [];
            let cur = '';
            let inQ = false;
            for (const ch of line + ',') {
                if (ch === '"') {
                    inQ = !inQ;
                } else if (ch === ',' && !inQ) {
                    values.push(cur.trim());
                    cur = '';
                } else {
                    cur += ch;
                }
            }
            const row: Record<string, string> = {};
            headers.forEach((h, i) => {
                row[h] = values[i] ?? '';
            });
            return row as unknown as AB_BookingRow;
        });
}

export function AB_parseDate(s: string): Date | null {
    if (!s) return null;
    const p = s.split('/');
    if (p.length !== 3) return null;
    const d = new Date(+p[2], +p[0] - 1, +p[1]);
    return isNaN(d.getTime()) ? null : d;
}

export function AB_formatDate(s: string): string {
    const d = AB_parseDate(s);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function AB_formatAmount(v: string | number): string {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return '—';
    return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
    }).format(n);
}

type AB_DeadlineVariant = 'past' | 'urgent' | 'warning' | 'ok';
export function AB_deadlineBadgeInfo(
    daysStr: string,
): { label: string; variant: AB_DeadlineVariant } | null {
    const d = parseInt(daysStr, 10);
    if (isNaN(d)) return null;
    if (d < 0) return { label: 'Overdue', variant: 'past' };
    if (d === 0) return { label: 'Today!', variant: 'urgent' };
    if (d <= 3) return { label: `In ${d}d`, variant: 'urgent' };
    if (d <= 7) return { label: `In ${d}d`, variant: 'warning' };
    return { label: `In ${d}d`, variant: 'ok' };
}

export function AB_statusBadgeClass(v: string): string {
    if (v === 'travelled') return 'fo-status-travelled';
    if (v === 'cancelled') return 'fo-status-cancelled';
    return 'fo-status-not-travelled';
}

export function AB_paymentBadgeInfo(v: string): { label: string; className: string } {
    if (v === 'fully_paid') return { label: 'Fully paid', className: 'fo-pay-solde' };
    if (v === 'account_done') return { label: 'Partially paid', className: 'fo-pay-partiel' };
    if (v === 'deposit_paid') return { label: 'Deposit paid', className: 'fo-pay-acompte' };
    return { label: v, className: 'fo-pay-acompte' };
}

export function AB_tripTagInfo(v: string): { label: string; className: string } | null {
    if (v === 'travelling')
        return { label: 'Travelling', className: 'fo-trip-tag fo-trip-travelling' };
    if (v === 'travelled')
        return { label: 'Completed', className: 'fo-trip-tag fo-trip-travelled' };
    return null;
}

export function AB_sortRows(
    rows: AB_BookingRow[],
    column: AB_SortColumn,
    direction: AB_SortDirection,
): AB_BookingRow[] {
    return [...rows].sort((a, b) => {
        const va = a[column];
        const vb = b[column];
        if (NUM_COLS.has(column)) {
            const na = parseFloat(va) || 0;
            const nb = parseFloat(vb) || 0;
            return direction === 'asc' ? na - nb : nb - na;
        }
        if (DATE_COLS.has(column)) {
            const ta = AB_parseDate(va)?.getTime() ?? 0;
            const tb = AB_parseDate(vb)?.getTime() ?? 0;
            return direction === 'asc' ? ta - tb : tb - ta;
        }
        const r = (va || '').toLowerCase().localeCompare((vb || '').toLowerCase());
        return direction === 'asc' ? r : -r;
    });
}

export function AB_filterRows(
    rows: AB_BookingRow[],
    search: string,
    paymentFilter: AB_PaymentFilter,
    showTravelled: boolean,
): AB_BookingRow[] {
    const q = search.toLowerCase().trim();
    return rows.filter(
        (r) =>
            (!q || r.request_id.includes(q) || r.country_name.toLowerCase().includes(q)) &&
            (!paymentFilter || r.status_name_legacy === paymentFilter) &&
            (showTravelled || r.status_grouped !== 'travelled'),
    );
}

type AB_Kpis = {
    total: number;
    stillCancellable: number;
    pastDeadline: number;
    expoNow: string;
    expoMax: string;
};
export function AB_computeKpis(rows: AB_BookingRow[]): AB_Kpis {
    const cancellable = rows.filter((r) => {
        const d = parseInt(r.days_until_deadline, 10);
        return r.status_grouped === 'not travelled' && !isNaN(d) && d >= 0;
    });
    const pastDeadline = rows.filter((r) => {
        const d = parseInt(r.days_until_deadline, 10);
        return !isNaN(d) && d < 0;
    });
    const expoNow = cancellable.reduce((s, r) => s + (parseFloat(r.amount_paid) || 0), 0);
    const expoMax = cancellable.reduce((s, r) => s + (parseFloat(r.trip_price) || 0), 0);
    return {
        total: rows.length,
        stillCancellable: cancellable.length,
        pastDeadline: pastDeadline.length,
        expoNow: AB_formatAmount(expoNow),
        expoMax: AB_formatAmount(expoMax),
    };
}

export function AB_rowsToCsv(rows: AB_BookingRow[]): string {
    const head = [
        'Booking #',
        'Destination',
        'Sale date',
        'Trip date',
        'Cancellation deadline',
        'Days remaining',
        'Payment',
        'Trip status',
        'Paid (€)',
        'Remaining (€)',
        'Total (€)',
    ];
    const payLabels: Record<string, string> = {
        fully_paid: 'Fully paid',
        account_done: 'Partially paid',
        deposit_paid: 'Deposit paid',
    };
    const tripLabels: Record<string, string> = {
        'not travelled': 'Upcoming',
        travelling: 'Travelling',
        travelled: 'Completed',
    };
    const body = rows.map((r) => [
        r.request_id,
        r.country_name,
        AB_formatDate(r.sale_date),
        AB_formatDate(r.trip_arrival_date),
        AB_formatDate(r.cancellation_deadline),
        r.days_until_deadline,
        payLabels[r.status_name_legacy] ?? r.status_name_legacy,
        tripLabels[r.status_grouped] ?? r.status_grouped,
        r.amount_paid,
        r.remaining_balance_to_pay,
        r.trip_price,
    ]);
    return '﻿' + [head, ...body].map((r) => r.join(';')).join('\n');
}

export function AB_agencyInitials(name: string): string {
    return name
        .replace(/[^A-Za-zÀ-ÿ]/g, '')
        .slice(0, 2)
        .toUpperCase();
}
